import type { MetricEntry } from '../core/MonitoringClient'
import { LogFormatter } from './LogFormatter'

export interface DataDogMetric {
  metric: string
  points: Array<[number, number]>
  tags: string[]
  type: 'gauge' | 'count' | 'histogram'
  interval?: number
}

export interface MetricFormatterConfig {
  metricPrefix?: string
  maxTagLength?: number
  maxTagCount?: number
}

export class MetricFormatter {
  static create(logFormatter?: LogFormatter, config?: MetricFormatterConfig): MetricFormatter {
    return new MetricFormatter(logFormatter ?? LogFormatter.create(), config)
  }

  private readonly config: Required<MetricFormatterConfig>

  constructor(
    private readonly logFormatter: LogFormatter,
    config?: MetricFormatterConfig
  ) {
    this.config = {
      metricPrefix: config?.metricPrefix ?? '',
      maxTagLength: config?.maxTagLength ?? 50,
      maxTagCount: config?.maxTagCount ?? 10,
    }
  }

  /**
   * Formats a metric for DataDog using optimized multi-metric approach
   * Returns 2 metrics: duration (gauge) and executions (count)
   */
  formatForDataDogMultiMetric(metric: MetricEntry): DataDogMetric[] {
    const timestamp = metric.timestamp || Date.now()
    const baseTimestamp = Math.floor(timestamp / 1000)
    const baseTags = [
      `use_case:${this.convertPascalToSnakeCase(metric.usecase)}`,
      ...this.extractTags(metric.metadata),
    ]

    return [
      // Métrica de duración
      {
        metric: `${this.config.metricPrefix}.usecase.duration`,
        points: [[baseTimestamp, metric.duration]],
        tags: [...baseTags, `success:${metric.success}`],
        type: 'gauge',
      },
      // Métrica de contador de ejecuciones
      {
        metric: `${this.config.metricPrefix}.usecase.executions`,
        points: [[baseTimestamp, 1]],
        tags: [...baseTags, `success:${metric.success}`],
        type: 'count',
      },
    ]
  }

  /**
   * Extracts tags from metadata for DataDog
   */
  extractTags(metadata?: Record<string, unknown>): string[] {
    if (!metadata) return []

    const tags = Object.entries(metadata)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        // Convert value to string and sanitize
        const stringValue = String(value)
          .replace(/[^a-zA-Z0-9_.-]/g, '_')
          .substring(0, this.config.maxTagLength) // Limit tag value length

        const sanitizedKey = key
          .replace(/[^a-zA-Z0-9_]/g, '_')
          .toLowerCase()
          .substring(0, 30) // Limit tag key length

        return `${sanitizedKey}:${stringValue}`
      })
      .slice(0, this.config.maxTagCount) // Limit number of tags

    return tags
  }

  /**
   * Converts PascalCase to snake_case
   * Example: GetCurrentMarketSharedUseCase -> get_current_market_shared_use_case
   */
  private convertPascalToSnakeCase(text: string): string {
    return text
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
      .replace(/([a-z\d])([A-Z])/g, '$1_$2')
      .toLowerCase()
  }
}
